/* Libs */
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

/* Containers */

/* Hooks */
import useOnboardingRedirect from '~/src/hooks/useOnboardingRedirect';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';

/* Regions */
import { getRegionalConfig } from '~/src/regions/config/config';

/* Routes */
import TemplateRoutes from './Templates';
import FormTemplateDrafting from './FormTemplateDrafting';
import AddDocumentRoutes from './AddDocument';
import SignatureRoutesContainer from './Signatures';
import { RequiresSubscription } from '~/src/containers/RequiresSubscription';
import Page from '~/src/components/PageLayout/Page';
import { SubscribePage } from '~/src/pages/Subscribe';
import { Subscribe as OldSubscribePage } from '~/src/pages/SubscribeOld';
import { Subscribe as CbsSubscribePage } from '~/src/pages/SubscribeCbs';
import { SettingsPage } from '~/src/pages/Settings';
import { ClioConnect } from '~/src/pages/ClioConnect';
import { ContactsPage } from '~/src/pages/Contacts';
import { MattersPage } from '~/src/pages/Matters';
import { LibraryPage } from '~/src/pages/Library';
import { DocumentsPage } from '~/src/pages/Documents';
import { DocumentSetsPage } from '~/src/pages/DocumentSets';
import { MatterDetailsPage } from '~/src/pages/MatterDetails';
import { SignaturesPage } from '~/src/pages/Signatures';
import { SignatureDetailPage } from '~/src/pages/SignaturePackage';
import { PopulatePage } from '~/src/pages/Populate';
import { ViewDocumentPage } from '~/src/pages/ViewDocument';
import { DraftingPage } from '~/src/pages/Drafting';
import ClioQuestionnaireRoutes from './ClioQuestionnaires';
import { WelcomeFormPage } from '~/src/pages/Welcome';
import { useUIFlagEnabled } from '~/src/entities/uiFlags';
import { ContentPackageWizard } from '~/src/pages/SubscribeOld/ContentPackageWizard';
import IdentityMigrationModal from '~/src/entities/identity/components/MigrationModal/IdentityMigrationModalContainer';
import RegenerateDocumentRedirect from '~/src/components/RegenerateDocumentRedirect';

import IdentityMigrationCompletedModal from '~/src/entities/identity/components/MigrationModal/IdentityMigrationCompletedModalContainer';

const Routes = () => {
  const { isClioQuestionnaireEnabled } = useFeatureAccesses();

  const isNewSubscribePageEnabled = useUIFlagEnabled('newSubscribePage');
  const { isCbsIntegrationEnabled } = useFeatureAccesses();
  const { isEsignEnabled } = getRegionalConfig();

  const redirectInfo = useOnboardingRedirect();

  const redirectComponent = isCbsIntegrationEnabled
    ? CbsSubscribePage
    : isNewSubscribePageEnabled
    ? SubscribePage
    : OldSubscribePage;

  return (
    <Switch>
      {redirectInfo.shouldRedirect && <Redirect to={redirectInfo.target} />}
      <Route
        exact
        path={['/welcome', '/welcome-free-trial']}
        component={WelcomeFormPage}
      />
      <Route exact path={'/subscribe'} component={redirectComponent} />

      {/* private routes */}
      <RequiresSubscription>
        <IdentityMigrationModal />
        <IdentityMigrationCompletedModal />
        <Switch>
          <Route
            path="/clio/connect"
            render={({ location }) => {
              const params = new URLSearchParams(location.search);
              const code = params.get('code');
              return code ? <ClioConnect code={code} /> : <Redirect to={'/'} />;
            }}
          />

          <Route path="/content-package-wizard">
            <ContentPackageWizard />
          </Route>

          <Route exact path="/matter">
            <MattersPage />
          </Route>

          <Route
            exact
            path="/matter/:matterId"
            render={({ location, match }) => (
              <MatterDetailsPage
                matterId={match.params.matterId}
                matterTitle={location.state?.title}
              />
            )}
          />

          <Route path="/library">
            <LibraryPage />
          </Route>
          <Redirect exact from="/templates" to="/library/templates" />

          <Route path="/templates">
            <TemplateRoutes />
          </Route>
          <Route
            exact
            path="/drafting/:projectId"
            render={({ match: { params } }) => {
              const { projectId } = params;
              return <DraftingPage projectId={parseInt(projectId)} />;
            }}
          />
          <Route path="/form-template">
            <Page showHeader={false} disablePadding={true}>
              <FormTemplateDrafting />
            </Page>
          </Route>

          <Route
            exact
            path="/populate/:projectId"
            render={(props) => {
              const { projectId } = props.match.params;
              let initialProject = false;
              if (props.location && props.location.state) {
                initialProject = props.location.state.initialProject;
              }
              return (
                <PopulatePage
                  projectId={parseInt(projectId)}
                  initialProject={initialProject}
                />
              );
            }}
          />

          <Route
            exact
            path="/populate/:projectId/document/:documentId"
            render={({ match }) => (
              <ViewDocumentPage
                projectId={match.params.projectId}
                documentId={match.params.documentId}
              />
            )}
          />

          <Route exact path="/contacts">
            <Page title="Contacts">
              <ContactsPage />
            </Page>
          </Route>

          <Route exact path="/documents">
            <DocumentsPage />
          </Route>

          <Route exact path="/document-sets">
            <DocumentSetsPage />
          </Route>

          {isEsignEnabled && (
            <Route exact path="/signatures">
              <SignaturesPage />
            </Route>
          )}

          {isEsignEnabled && (
            <Route
              exact
              path="/signatures/details/:packageId/"
              render={({ match: { params } }) => (
                <SignatureDetailPage signaturePackageId={params.packageId} />
              )}
            />
          )}

          {isEsignEnabled && (
            <Route path="/signatures/prox">
              <Page
                disablePadding={true}
                showNavigation={false}
                showHeader={false}
              >
                <SignatureRoutesContainer />
              </Page>
            </Route>
          )}
          {isEsignEnabled && (
            <Route path="/signatures">
              <Page>
                <SignatureRoutesContainer />
              </Page>
            </Route>
          )}
          {isEsignEnabled && (
            <Route path="/signature-wizard">
              <Page
                disablePadding={true}
                showNavigation={false}
                showHeader={false}
              >
                <SignatureRoutesContainer />
              </Page>
            </Route>
          )}
          {isEsignEnabled && (
            <Route path="/custom-signature-package">
              <Page
                disablePadding={true}
                showNavigation={false}
                showHeader={false}
              >
                <SignatureRoutesContainer />
              </Page>
            </Route>
          )}

          <Route path="/add-document">
            <AddDocumentRoutes />
          </Route>

          <Route path="/settings">
            <SettingsPage />
          </Route>
          {isClioQuestionnaireEnabled && (
            <Route path="/questionnaires">
              <ClioQuestionnaireRoutes />
            </Route>
          )}

          <Route exact path="/regenerate/document/:documentId">
            <RegenerateDocumentRedirect />
          </Route>

          <Route
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: '/library',
                  search: location.search,
                }}
              />
            )}
          />
        </Switch>
      </RequiresSubscription>
    </Switch>
  );
};

export default Routes;
